import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    List,
    Box,
    Typography,
    Avatar,
    CircularProgress,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// ─── Types matching the API response shape ─────────────────────────────────────
// POST /auth/exchange-instagram-portfolio-token returns:
// {
//   "data": {
//     "accounts": [
//       {
//         "page_id": "773197932553074",
//         "page_name": "New Test Page",
//         "page_access_token": "...",
//         "page_profile_picture": "https://...",
//         "instagram_business_account": {
//           "id": "17841480433341002",
//           "username": "devs.test.27",
//           "profile_picture_url": null,
//           "followers_count": 0
//         }
//       }
//     ]
//   }
// }

export interface InstagramAccount {
    /** Facebook Page id (used as the connect key) */
    page_id: string;
    /** Facebook Page name (shown as subtitle) */
    page_name: string;
    /** Facebook Page access token passed to the connect API */
    page_access_token: string;
    /** Page tasks from the list step, sent back so the connect check needs no extra Graph call */
    page_tasks?: string[];
    /** Facebook Page profile picture (fallback avatar) */
    page_profile_picture: string;
    /** Nested IG business/creator account info */
    instagram_business_account: {
        id: string;
        username: string;
        profile_picture_url: string | null;
        followers_count: number;
    };
}

interface InstagramPageSelectionModalProps {
    open: boolean;
    onClose: () => void;
    /** List of accounts from `data.accounts` in the API response */
    pages: InstagramAccount[];
    /** Called with the selected account when user clicks "Connect" */
    onSelectPage: (page: InstagramAccount) => void;
    /** page_id currently being connected (shows spinner + disables all other rows) */
    connectingPageId: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const InstagramPageSelectionModal: React.FC<InstagramPageSelectionModalProps> = ({
    open,
    onClose,
    pages,
    onSelectPage,
    connectingPageId,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 'var(--large-border-radius)',
                    maxHeight: '80vh',
                },
            }}
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: "26px", fontWeight: 700, fontFamily: "var(--font-family-manrope)", color: "#111827", lineHeight: 1.3 }}>
                        Select Instagram Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Choose an Instagram business or creator account to connect
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={!!connectingPageId}
                    sx={{ color: 'text.secondary', '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* ── Body ── */}
            <DialogContent sx={{ p: 0 }}>
                <List sx={{ py: 0 }}>
                    {pages.map((page, index) => {
                        const igAccount = page.instagram_business_account;
                        const isConnecting = connectingPageId === page.page_id;
                        const isDisabled = !!connectingPageId;
                        return (
                            <Box
                                key={page.page_id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 2,
                                    px: 3,
                                    borderBottom: index !== pages.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    '&:hover': { backgroundColor: '#fdf5ff' },
                                }}
                            >
                                {/* ── Left: Avatar + account info ── */}
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    {/* Instagram gradient ring around avatar */}
                                    <Box
                                        sx={{
                                            p: '2px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                                            mr: 2,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Avatar
                                            src={igAccount.profile_picture_url || undefined}
                                            alt={igAccount.username}
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                border: '2px solid #fff',
                                                background: igAccount.profile_picture_url
                                                    ? 'transparent'
                                                    : 'linear-gradient(135deg, #f9ce34, #ee2a7b, #6228d7)',
                                                fontSize: 22,
                                            }}
                                        >
                                            {/* Fallback: first letter of username */}
                                            {!igAccount.profile_picture_url && (igAccount.username?.[0]?.toUpperCase() || 'I')}
                                        </Avatar>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {/* IG username (bold) */}
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            noWrap
                                            sx={{
                                                background: 'linear-gradient(90deg, #dc2743, #bc1888)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                            }}
                                        >
                                            {igAccount.username}
                                        </Typography>

                                        {/* FB Page name as subtitle */}
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                                            via&nbsp;<strong>{page.page_name}</strong>
                                        </Typography>

                                        {/* Follower count */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {igAccount.followers_count.toLocaleString()} followers
                                            </Typography>
                                            {/* <Chip
                                                label="Business Account"
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: 10,
                                                    background: 'linear-gradient(45deg, #f09433, #bc1888)',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                }}
                                            /> */}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* ── Right: Connect button ── */}
                                <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={isDisabled}
                                    onClick={() => onSelectPage(page)}
                                    sx={{
                                        ml: 2,
                                        minWidth: 110,
                                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                        color: '#fff',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        boxShadow: 'none',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #d17d2d 0%, #c85a34 25%, #b92139 50%, #a91d5a 75%, #991476 100%)',
                                            boxShadow: 'none',
                                        },
                                        '&.Mui-disabled': {
                                            background: isConnecting
                                                ? 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
                                                : '#e0e0e0',
                                            color: isConnecting ? '#fff' : '#999',
                                        },
                                    }}
                                    startIcon={
                                        isConnecting && (
                                            <CircularProgress size={16} thickness={4} sx={{ color: '#fff' }} />
                                        )
                                    }
                                >
                                    {isConnecting ? 'Connecting...' : 'Connect'}
                                </Button>
                            </Box>
                        );
                    })}
                </List>

                {/* ── Empty state ── */}
                {pages.length === 0 && (
                    <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No Instagram accounts found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Make sure your Instagram business or creator account is connected to a Facebook Page
                            (Facebook Page &gt; Settings &gt; Instagram).
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InstagramPageSelectionModal;
